/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo, useRef, useState } from "react";
import { BEM } from "@adapptio/lib-components";
import { isFunction, isHTMLElement, isNil, isUndefined } from "@adapptio/lib-shared";
import { FloatingPanelHeader } from "./FloatingPanelHeader";
import { IUseDragHandlers, useDrag } from "../../hooks/useDrag";
import {
	findClosestFloatingPanelBorder,
	findNestedFloatingPanelDragHandle,
	floatingPanelDragHandleProps
} from "./floatingPanelUtils";
import { IFloatingPanelRect } from "./FloatingPanelModel";
import { FloatingPanelResizers, IFloatingPanelResizersProps } from "./FloatingPanelResizers";

export interface IFloatingPanelProps {
	className?: string;
	title?: React.ReactNode;
	preventDrag?: boolean;
	onClose?: () => void;
	rect?: IFloatingPanelRect;
	onRectChange?: (rect: IFloatingPanelRect) => void;
}

const BORDER_OFFSET = {
	top: 0,
	right: -40,
	bottom: 0,
	left: 40
};

const MIN_SIZE = {
	width: 120,
	height: 120
};

export const FloatingPanel: React.FC<IFloatingPanelProps> = ({
	children,
	className,
	title,
	preventDrag,
	onClose,
	rect: rectProp,
	onRectChange
}) => {
	const floatingPanelRef = useRef<HTMLDivElement>();
	const [ defaultRect, setDefaultRect ] = useState<IFloatingPanelRect>(rectProp || null);

	const dragStartValues = useRef({
		startX: 0,
		startY: 0,
		startWidth: 0,
		startHeight: 0
	});

	const useDefaultRect = useMemo(
		() => isUndefined(rectProp) || isUndefined(onRectChange),
		[ rectProp, onRectChange ]
	);

	/** Safe rect */
	const calculateSafeRect = useCallback<(rect: IFloatingPanelRect) => IFloatingPanelRect>((rect) => {
		const border = findClosestFloatingPanelBorder(floatingPanelRef.current);
		const handle = findNestedFloatingPanelDragHandle(floatingPanelRef.current);

		if (!isHTMLElement(border) || !isHTMLElement(handle)) {
			return rect;
		}

		const safeRect = { ...rect };

		const borderRect = border.getBoundingClientRect();
		const handleRect = handle.getBoundingClientRect();

		const borderTop = 0;
		const borderRight = borderRect.width;
		const borderBottom = borderRect.height;
		const borderLeft = 0;

		const borderWidth = borderRect.width;
		const borderHeight = borderRect.height;

		const handleWidth = handleRect.width;
		const handleHeight = handleRect.height;

		const minX = borderLeft - handleWidth + BORDER_OFFSET.left;
		const maxX = borderRight + BORDER_OFFSET.right;
		const minY = borderTop + BORDER_OFFSET.top;
		const maxY = borderBottom - handleHeight + BORDER_OFFSET.bottom;

		const minWidth = MIN_SIZE.width;
		const maxWidth = borderWidth;
		const minHeight = MIN_SIZE.height;
		const maxHeight = borderHeight;

		if (!isNil(rect.x)) {
			safeRect.x = Math.min(maxX, Math.max(minX, rect.x));
		}

		if (!isNil(rect.y)) {
			safeRect.y = Math.min(maxY, Math.max(minY, rect.y));
		}

		if (!isNil(rect.width)) {
			const width = isNil(rect.x) ? rect.width : rect.width - (safeRect.x - rect.x);
			safeRect.width = Math.min(maxWidth, Math.max(minWidth, width));
		}

		if (!isNil(rect.height)) {
			const height = isNil(rect.y) ? rect.height : rect.height - (safeRect.y - rect.y);
			safeRect.height = Math.min(maxHeight, Math.max(minHeight, height));
		}

		return { ...safeRect };
	}, []);

	const rect = useDefaultRect ? defaultRect : calculateSafeRect(rectProp);

	const updateRect = useCallback<(rectUpdate: IFloatingPanelRect) => void>(
		(rectUpdate) => {
			if (useDefaultRect) {
				setDefaultRect((prevRect) => ({
					...prevRect,
					...rectUpdate
				}));
			}

			if (isFunction(onRectChange)) {
				onRectChange({ ...rect, ...rectUpdate });
			}
		},
		[ useDefaultRect, onRectChange, rect ]
	);

	/** Close */
	const handleClose = useCallback(() => {
		if (isFunction(onClose)) {
			onClose();
		}
	}, [ onClose ]);

	const getInitRect = useCallback<() => IFloatingPanelRect>(() => {
		const panelRect = floatingPanelRef.current.getBoundingClientRect();
		const parentRect = floatingPanelRef.current.parentElement.parentElement.getBoundingClientRect();

		const initValues = { ...(rect || {}) };

		if (isNil(initValues.x)) {
			initValues.x = panelRect.x - parentRect.x;
		}

		if (isNil(initValues.y)) {
			initValues.y = panelRect.y - parentRect.y;
		}

		if (isNil(initValues.width)) {
			initValues.width = panelRect.width;
		}

		if (isNil(initValues.height)) {
			initValues.height = panelRect.height;
		}

		return initValues;
	}, [ rect ]);

	const onDragOrResizeStart = useCallback<IUseDragHandlers["onDragStart"]>(() => {
		const initRect = getInitRect();

		dragStartValues.current = {
			startX: initRect.x,
			startY: initRect.y,
			startWidth: initRect.width,
			startHeight: initRect.height
		};
	}, [ getInitRect ]);

	/** Drag/Move panel */
	const onDragMove = useCallback<IUseDragHandlers["onDragMove"]>(
		(event, data) => {
			event.preventDefault();

			const { movedBy } = data;

			const { startX, startY } = dragStartValues.current;

			const { x, y } = calculateSafeRect({
				x: startX + movedBy.x,
				y: startY + movedBy.y
			});

			updateRect({ x, y });
		},
		[ updateRect, calculateSafeRect ]
	);

	const { startDragging, dragging } = useDrag(
		{
			onDragStart: onDragOrResizeStart,
			onDragMove
		},
		{ preventTextSelection: true }
	);

	// Resize
	const onResize = useCallback<IFloatingPanelResizersProps["onResize"]>(
		(event, data) => {
			const { resizerPosition, movedBy } = data;
			const { startX, startY, startWidth, startHeight } = dragStartValues.current;

			const rectUpdate: IFloatingPanelRect = {
				x: startX,
				y: startY
			};

			if (resizerPosition.top) {
				rectUpdate.height = startHeight - movedBy.y;
				rectUpdate.y = startY + movedBy.y;
			}

			if (resizerPosition.left) {
				rectUpdate.width = startWidth - movedBy.x;
				rectUpdate.x = startX + movedBy.x;
			}

			if (resizerPosition.bottom) {
				rectUpdate.height = startHeight + movedBy.y;
			}

			if (resizerPosition.right) {
				rectUpdate.width = startWidth + movedBy.x;
			}

			updateRect(calculateSafeRect(rectUpdate));
		},
		[ rect, updateRect ]
	);

	const style = useMemo<React.CSSProperties>(() => {
		if (rect) {
			const safeRect = calculateSafeRect(rect);

			const top = isNil(safeRect.y) ? "50%" : safeRect.y;
			const left = isNil(safeRect.x) ? "50%" : safeRect.x;
			const width = isNil(safeRect.width) ? "auto" : safeRect.width;
			const height = isNil(safeRect.height) ? "auto" : safeRect.height;

			const styles: React.CSSProperties = {
				top,
				left,
				width,
				height
			};

			const offset: Record<"x" | "y", string | number> = {
				x: 0,
				y: 0
			};

			if (styles.top === "50%") {
				offset.y = isNil(safeRect.height) ? "-50%" : (safeRect.height / 2) * -1;
			}

			if (styles.left === "50%") {
				offset.x = isNil(safeRect.width) ? "-50%" : (safeRect.width / 2) * -1;
			}

			// transform will break code editor's suggestion widget position
			if (`${offset.y}`.endsWith("%") || `${offset.x}`.endsWith("%")) {
				styles.transform = `translate(${offset.x}, ${offset.y})`;
			} else {
				styles.marginTop = offset.y;
				styles.marginLeft = offset.x;
			}

			return styles;
		}

		return {
			top: "50%",
			left: "50%",
			transform: "translate(-50%, -50%)"
		};
	}, [ rect?.x, rect?.y, rect?.width, rect?.height, calculateSafeRect ]);

	const baseClass = BEM("edt-floating-panel", null, {
		draggable: !preventDrag,
		dragging
	});

	if (className) {
		baseClass.add(className);
	}

	return (
		<div className={baseClass.toClassName()} style={style} ref={floatingPanelRef}>
			<FloatingPanelResizers onResize={onResize} onResizeStart={onDragOrResizeStart} />
			<div className={baseClass.element("inner").toClassName()}>
				<FloatingPanelHeader
					onMouseDown={startDragging}
					{...floatingPanelDragHandleProps}
					onClose={handleClose}
				>
					{title}
				</FloatingPanelHeader>
				{children}
			</div>
		</div>
	);
};
